import { formatFractionalString } from '@common/utils/formatFractionalString';
import { NormalizerFn } from '../types';

interface RangeRule {
    id: string;
    min: number;
    max: number;
}

const FPS_RULES: RangeRule[] = [
    {
        id: '23.98',
        min: 23.9,
        max: 23.989,
    },
    {
        id: '29.97',
        min: 29.9,
        max: 29.979,
    },
    {
        id: '59.94',
        min: 59.9,
        max: 59.949,
    },
];

export const fpsNormalizer: NormalizerFn = (value) => {
    if (!value || parseFloat(value) === 0) return 'missing';

    const num = parseFloat(formatFractionalString(value, 3));
    if (isNaN(num)) return value;

    const rule = FPS_RULES.find((r) => num >= r.min && num <= r.max);
    if (rule) return rule.id;

    return Math.round(num).toString();
};

const RESOLUTION_RULES: RangeRule[] = [
    { id: '_sd', min: 0, max: 799 },
    { id: '_fhd', min: 1900, max: 1950 },
    { id: '_2k', min: 1951, max: 2050 },
    { id: '_uhd', min: 3800, max: 3850 },
    { id: '_4k', min: 3851, max: 4100 },
];

// const RESOLUTION_BUCKETS = [
//     { key: 'hhd', width: 15360, height: 8640 },
//     { key: 'uwfuhd', width: 10080, height: 4320 },
//     { key: 'fuhd', width: 7680, height: 4320 },
//     { key: 'uwuhd', width: 5120, height: 2160 },
//     { key: 'wuhd', width: 5040, height: 2160 },
//     { key: 'uwqhd', width: 3440, height: 1440 },
//     { key: 'uhd', width: 3840, height: 2160 },
//     { key: 'qhd', width: 2560, height: 1440 },
//     { key: 'uwfhd', width: 2560, height: 1080 },
//     { key: 'fhd', width: 1920, height: 1080 },
//     { key: 'hd', width: 1280, height: 720 },
// ];

export const resolutionNormalizer: NormalizerFn = (value) => {
    if (!value) return 'missing';

    const match = value.match(/(\d+)x(\d+)/);
    if (!match) return value;

    const w = parseInt(match[1], 10);
    const rule = RESOLUTION_RULES.find((r) => w >= r.min && w <= r.max);
    if (rule) return rule.id;

    return '_others';
};

const SUBTITLES_PREFIXES = [
    'sst',
    'rtf',
    'tex',
    'pac',
    'srt',
    'stl',
    'ttml',
    'subrip',
    'asc',
    'cap',
    'doc',
    'ezt',
    'otf',
    'ttf',
    'txt',
    'cheeta',
    'cheetah',
    'dc subtitle',
    'eia-608',
    'lambda',
    'n19',
];

const CODEC_RULES = [
    {
        id: '_subtitles',
        matches: (v: string) => SUBTITLES_PREFIXES.includes(v),
    },
    { id: '_jpeg', matches: (v: string) => v.includes('jpeg') },
    { id: '_mpeg2', matches: (v: string) => v.includes('mpeg-2') },
    { id: '_audio_pcm', matches: (v: string) => v.includes('pcm') },
    { id: '_avc', matches: (v: string) => v.includes('avc') },
    { id: '_prores', matches: (v: string) => v.includes('prores') },
    { id: '_dpx', matches: (v: string) => v.includes('dpx') },
];

export const codecNormalizer: NormalizerFn = (value) => {
    if (!value) return 'missing';

    const val = value.toLowerCase().trim();
    const rule = CODEC_RULES.find((s) => s.matches(val));
    if (rule) return rule.id;

    return value;
};

const CONTAINER_RULES = [
    { id: '_dsm', matches: (v: string) => v.includes('dsm') },
    { id: '_dcp', matches: (v: string) => v.includes('dcp') },
    { id: '_quicktime', matches: (v: string) => v.includes('quicktime') },
    { id: '_mpeg', matches: (v: string) => v.includes('mpeg') },
    { id: '_avi', matches: (v: string) => v.includes('avi') },
    { id: '_mp4', matches: (v: string) => v.includes('mp4') },
    { id: '_zip', matches: (v: string) => v.includes('zip') },
    { id: '_base media', matches: (v: string) => v.includes('base media') },

    ...CODEC_RULES,
];

export const containerNormalizer: NormalizerFn = (value) => {
    if (!value) return 'missing';

    const match = value.match(/(.*)\s\(\.[\w]+\/[\w]+\)/);
    const val = (match?.[1] || value).toLowerCase();
    const rule = CONTAINER_RULES.find((s) => s.matches(val));
    if (rule) return rule.id;

    return value;
};

export const NORMALIZATION_STRATEGIES: Record<string, NormalizerFn> = {
    fps: fpsNormalizer,
    resolution: resolutionNormalizer,
    codec: codecNormalizer,
    container: containerNormalizer,
};

export type ResolutionId = (typeof RESOLUTION_RULES)[number]['id'];
export type CodecId = (typeof CODEC_RULES)[number]['id'];
export type ContainerId = (typeof CONTAINER_RULES)[number]['id'];
export type FpsId = (typeof FPS_RULES)[number]['id'];

export type NormalizedFacetId = ResolutionId | CodecId | ContainerId | FpsId;
